import { useFormikContext } from 'formik';
import { useCallback, useMemo, Dispatch, SetStateAction } from 'react';
import { FormPart } from '@modules/shared/components/FormPart';
import { IGapLoanPaidOffRequestInformation } from '@modules/admin/Tools/shared/interfaces';
import { GAP_PRODUCT_TYPE } from '@modules/ContactManager/components/Tabs/components/Deals/components/CheckRequestForm';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { TextInputField } from '@shared/fields/TextInputField';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { getFormattedPayeeOptions, LOAN_PAID_OFF } from '@modules/accounting/utils';
import { IOption } from '@shared/interfaces';
import { productTypes } from '@modules/admin/Tools/LenderUtility/shared/data';
import { IPayeeLender, cancellationReasons } from '../interfaces';
import { IDealForCheckRequest } from './ImportProductCancel';

interface IUnIdentifiedDealsFormProps {
  lenderOptions: IOption[];
  lendersError: boolean;
  payeeData: IPayeeLender[];
  isChecked: boolean;
  setChecked: Dispatch<SetStateAction<boolean>>;
}

export const UnIdentifiedDealsForm = ({
  lenderOptions,
  lendersError,
  payeeData = [],
  isChecked,
  setChecked,
}: IUnIdentifiedDealsFormProps) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext<IDealForCheckRequest>();
  
  const formatAddress = (obj: IPayeeLender | IGapLoanPaidOffRequestInformation) => {
    const address = obj?.payeeAddress ?? '';
    const state = obj?.payeeState ? ` ${obj.payeeState}` : '';
    const zip = obj?.payeeZip ? ` ${obj.payeeZip}` : '';
    return `${address}${state}${zip}`;
  };

  const formattedPayeeOptions = useMemo(() => {
    return getFormattedPayeeOptions(payeeData, values?.product, values?.cancelReason);
  }, [payeeData, values?.product, values?.cancelReason]);

  const handleChange = useCallback(() => {
    setChecked(!isChecked);
    setFieldValue('address', '');
    setFieldValue('payeeName', '');
  }, [isChecked]);

  const handlePayeeChange = (payeeName: string) => {
    setFieldTouched('payeeName', false, false);
    if (!payeeName || !setFieldValue) return;

    let payeeAddress = '';

    for (const payee of payeeData) {
      if (payee.payeeName === payeeName) {
        payeeAddress = formatAddress(payee);
        break;
      }

      const isGapLoanPaidOff =
        values.product?.toLowerCase() === GAP_PRODUCT_TYPE.toLowerCase() &&
        values.cancelReason?.toLowerCase() === LOAN_PAID_OFF.toLowerCase();

      if (isGapLoanPaidOff) {
        const gapInfo = payee.gapLoanPaidOffRequestInformation;
        if (gapInfo && gapInfo.payeeName === payeeName) {
          payeeAddress = formatAddress(gapInfo);
          break;
        }
      }
    }

    setFieldValue('payeeName', payeeName);

    setFieldValue('address', payeeAddress);
  };

  const formConfig = useMemo(
    () =>
      [
        {
          name: 'product',
          label: 'Type',
          options: productTypes,
          component: SelectAutocompleteField,
        },
        {
          name: 'provider',
          label: 'Provider',
          component: TextInputField,
          value: 'NAC',
          disabled: true,
        },
        {
          name: 'lenderName',
          label: 'Lender',
          options: lenderOptions,
          component: SelectAutocompleteField,
        },
        {
          name: 'enterPayee',
          label: 'Enter Payee',
          component: CheckboxField,
          field: { onChange: handleChange, value: isChecked },
        },
        {
          name: 'payeeName',
          label: 'Payee',
          options: formattedPayeeOptions,
          component: isChecked ? TextInputField : SelectAutocompleteField,
          onChange: isChecked
            ? (ev: { target: { value: string } }) => setFieldValue('payeeName', ev.target.value)
            : (value: string) => handlePayeeChange(value),
        },
        {
          name: 'address',
          label: 'Address',
          component: TextInputField,
          disabled: !isChecked,
          onChange: (ev: { target: { value: string } }) => setFieldValue('address', ev.target.value),
        },
        {
          name: 'customerRefund',
          label: 'Customer Amount Refund',
          component: NumberInputField,
        },
        {
          name: 'refundFromSeller',
          label: 'Seller Amount Refund',
          component: NumberInputField,
        },
        {
          name: 'amountRequested',
          label: 'Amount Requested',
          component: NumberInputField,
        },
        {
          name: 'cancelDate',
          label: 'Cancelled Date',
          component: SelectDateField,
        },
        {
          name: 'referenceNumber',
          label: 'ACH REF NUMBER',
          component: TextInputField,
        },
        {
          name: 'relatedDeal',
          label: 'Related Deal',
          component: TextInputField,
        },
        {
          name: 'cancelReason',
          label: 'Reason for Cancellation',
          options: cancellationReasons,
          component: SelectAutocompleteField,
        },
        {
          name: 'comment',
          label: 'Request Comment',
          component: TextInputField,
        },
      ].map((field) => {
        if (field.name !== 'enterPayee') {
          return { ...field, width: '90%' };
        }
        return { ...field };
      }),
    [handleChange, isChecked, handlePayeeChange, payeeData, lendersError, formattedPayeeOptions]
  );

  return <FormPart config={formConfig} />;
};
